import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { useParams } from 'react-router-dom';
import { useTemaClaroPublico } from '../lib/theme';
import { fmtBytes, fmtDuration } from '../lib/atrVideos';

/**
 * LA PÁGINA QUE ABRE EL ADJUSTER — /p/atr/<token>, sin login, fuera del AppShell.
 *
 * Pedido del dueño (2026-09-17): "el link cuando abra sea un link fácil que no tenga ninguna
 * restricción y diga Proactive Public Adjusting y tenga el nombre que le van a poner los chicos".
 * Es lo que antes era el link de YouTube: la marca, el nombre que le puso la oficina y el video.
 * Nada más (ni el homeowner, ni el claim): lo que se quiera contar va en el nombre.
 *
 * El video se reproduce DIRECTO desde R2 con una URL firmada de 6 h (soporta rangos: se puede
 * saltar). Si el navegador no puede con el códec (HEVC en Chrome/Edge de Windows) o la firma
 * venció, se ofrece bajarlo. Siempre en claro. Un token inválido o revocado muestra el MISMO
 * mensaje, a propósito. En inglés: es para adjusters de seguros.
 */
type Vista = {
  company: string; title: string; fileName: string; mime: string; codec: string | null;
  sizeBytes: number | null; durationMs: number | null; width: number | null; height: number | null;
  sharedAt: string | null; mediaUrl: string; downloadUrl: string;
};

const fecha = (s: string) => new Date(s).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export function PublicAtrVideo() {
  const { t } = useI18n();
  useTemaClaroPublico();
  const { token } = useParams();
  const [v, setV] = useState<Vista | null>(null);
  const [muerto, setMuerto] = useState(false);
  const [fallo, setFallo] = useState(false);
  const reintentado = useRef(false);

  const cargar = () => fetch(`/api/public/atr/${token}`).then((r) => (r.ok ? r.json() : Promise.reject(new Error('no')))) as Promise<Vista>;

  useEffect(() => {
    // fetch directo y no el cliente del app: acá no hay sesión ni la tiene que haber.
    cargar()
      .then((d) => {
        setV(d);
        document.title = `${d.title} · ${d.company}`;
        // Una apertura = una vista (la oficina ve "Opened 2×"). Best-effort.
        fetch(`/api/public/atr/${token}/viewed`, { method: 'POST' }).catch(() => {});
      })
      .catch(() => setMuerto(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /** El <video> falló: si la URL firmada venció (la pestaña quedó abierta horas), se pide una nueva UNA vez. */
  function onError() {
    if (!reintentado.current) {
      reintentado.current = true;
      cargar().then((d) => { setV(d); setFallo(false); }).catch(() => setFallo(true));
      return;
    }
    setFallo(true);
  }

  const marco = { minHeight: '100vh', background: '#f4f5f7', padding: '28px 14px', fontFamily: 'var(--font-sans)', color: '#1f2430' } as const;
  const tarjeta = { maxWidth: 960, margin: '0 auto', background: '#fff', border: '1px solid #e4e6ee', borderRadius: 12, overflow: 'hidden' } as const;
  const [marca, ...restoMarca] = (v?.company ?? 'Proactive Public Adjusting Group').split(' ');
  const cabecera = (
    <div style={{ background: '#151a2d', padding: '18px 28px' }}>
      <div style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>{marca}</div>
      <div style={{ color: '#9aa3c0', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase' }}>{restoMarca.join(' ') || 'Public Adjusting Group'}</div>
    </div>
  );

  if (muerto) {
    return (
      <div style={marco}><div style={{ ...tarjeta, maxWidth: 620 }}>{cabecera}<div style={{ height: 3, background: '#5b5bd6' }} />
        <div style={{ padding: 28 }}>
          <div style={{ fontSize: 19, fontWeight: 700, marginBottom: 6 }}>{t('patr.dead')}</div>
          <div style={{ color: '#6b7180', fontSize: 14 }}>{t('patr.deadHelp')}</div>
        </div>
      </div></div>
    );
  }
  if (!v) return <div style={{ ...marco, display: 'grid', placeItems: 'center' }}><span style={{ color: '#6b7180' }}>{t('common.loadingDots')}</span></div>;

  const meta = [v.sharedAt ? `Shared ${fecha(v.sharedAt)}` : null, fmtDuration(v.durationMs) || null, v.width && v.height ? `${v.width}×${v.height}` : null, fmtBytes(v.sizeBytes) || null].filter(Boolean).join(' · ');
  const vertical = Boolean(v.width && v.height && v.height > v.width);

  return (
    <div style={marco}>
      <div style={tarjeta}>
        {cabecera}
        <div style={{ height: 3, background: '#5b5bd6' }} />
        <div style={{ padding: '22px 28px 26px', fontSize: 15 }}>
          <div style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: '#6b7180', fontWeight: 600 }}>{t('patr.kicker')}</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '4px 0 2px', lineHeight: 1.25 }}>{v.title}</h1>
          {meta && <div style={{ fontSize: 13, color: '#6b7180', marginBottom: 16 }}>{meta}</div>}
          {!fallo && (
            <video
              key={v.mediaUrl} controls playsInline preload="metadata" src={v.mediaUrl} onError={onError}
              style={{ width: '100%', maxHeight: vertical ? '78vh' : '70vh', background: '#000', borderRadius: 10, display: 'block' }}
            />
          )}
          {(fallo || v.codec === 'hevc') && (
            <div style={{ marginTop: 12, background: '#fff7ed', border: '1px solid #fdba74', color: '#7c2d12', borderRadius: 10, padding: '10px 14px', fontSize: 14, lineHeight: 1.5 }}>
              {fallo
                ? 'This browser could not play the video. Download it and open it with QuickTime, VLC or Windows Media Player.'
                : 'Recorded in HEVC (High Efficiency). If it does not play in this browser, download it and open it with QuickTime, VLC or Windows Media Player.'}
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <a href={v.downloadUrl} style={{ display: 'inline-block', background: '#151a2d', color: '#fff', textDecoration: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14 }}>{t('patr.download')}</a>
            <span style={{ fontSize: 12, color: '#6b7180' }}>{v.fileName}</span>
          </div>
        </div>
        <div style={{ background: '#fafbfe', borderTop: '1px solid #e4e6ee', padding: '14px 28px', fontSize: 12, color: '#6b7180', lineHeight: 1.6 }}>
          <div>{t('patr.sharedBy')} <b style={{ color: '#1f2430' }}>{v.company}</b> {t('patr.forClaim')}</div>
          <div style={{ marginTop: 4 }}><a href="https://www.claimsproactive.com" style={{ color: '#5b5bd6', textDecoration: 'none' }}>www.claimsproactive.com</a></div>
        </div>
      </div>
    </div>
  );
}
